// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/MemberTable.js
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faUsersGear, faPeopleArrows, faKey, faIdBadge } from '@fortawesome/free-solid-svg-icons';
import { getSelectedLanguage } from '../api/api';
import { languageManager } from '../utils/loadLanguage';
import useFetchStatuses from './useFetchStatuses';
import { fetchPositions, updateRole, updateStatus } from '../utils/HandlersMember';
import 'bootstrap/dist/css/bootstrap.min.css';

const MemberTable = ({ memberData, onEditMember, token, onViewSpecific, navigate }) => {
  const [positions, setPositions] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState({});
  const [selectedStatuses, setSelectedStatuses] = useState({});
  const [translations, setTranslations] = useState({});
  const { statuses, isLoading: isLoadingStatuses, error: errorStatuses } = useFetchStatuses(token);

  useEffect(() => {
    async function loadLang() {
      const lang = getSelectedLanguage();
      await languageManager.loadLanguage(lang);
      setTranslations(languageManager.translations.memberTable || {});
    }
    loadLang();
  }, []);

  useEffect(() => {
    const loadPositions = async () => {
      try {
        if (!token) return;
        const data = await fetchPositions(token); // ใช้จาก HandlersMember
        setPositions(data);
      } catch (error) {
        // ข้อผิดพลาดถูกจัดการใน HandlersMember
      }
    };
    loadPositions();

    if (memberData && memberData.length > 0) {
      const initialPositions = memberData.reduce((acc, member) => {
        if (member && typeof member.user_ID !== 'undefined') {
          acc[member.user_ID] = member.position_ID;
        }
        return acc;
      }, {});
      setSelectedPositions(initialPositions);

      const initialStatuses = memberData.reduce((acc, member) => {
        if (member && typeof member.user_ID !== 'undefined') {
          acc[member.user_ID] = member.status_ID;
        }
        return acc;
      }, {});
      setSelectedStatuses(initialStatuses);
    } else {
      setSelectedPositions({});
      setSelectedStatuses({});
    }
  }, [memberData, token]);

  const handleStatusUpdate = async (userId, newStatusId) => {
    if (!token) {
      console.error("Token missing, cannot update status.");
      return;
    }
    try {
      await updateStatus(userId, newStatusId, token); // ใช้จาก HandlersMember
      setSelectedStatuses(prev => ({ ...prev, [userId]: newStatusId }));
    } catch (error) {
      // ข้อผิดพลาดถูกจัดการใน HandlersMember
    }
  };

  const renderStatusDropdown = (userId) => {
    if (isLoadingStatuses || !Array.isArray(statuses)) {
      return <span className="text-muted small">{translations.loadingStatuses || 'Loading...'}</span>;
    }
    if (errorStatuses) {
      return <span className="text-danger small">{translations.errorLoadingStatuses || 'Error'}</span>;
    }
    const currentStatusId = selectedStatuses[userId];
    const currentStatus = statuses.find(s => s.id === currentStatusId);
    const currentStatusName = currentStatus ? currentStatus.name : (translations.selectStatus || "Select Status");

    return (
      <div className="dropdown">
        <button className="btn btn-warning dropdown-toggle btn-sm" type="button" data-bs-toggle="dropdown" aria-expanded="false">
          <FontAwesomeIcon icon={faPeopleArrows} /> {currentStatusName}
        </button>
        <ul className="dropdown-menu">
          {statuses.map((status) => (
            <li key={status.id}>
              <a
                className={`dropdown-item ${status.id === currentStatusId ? 'active' : ''}`}
                href="#!"
                onClick={(event) => {
                  event.preventDefault();
                  if (status.id !== currentStatusId) {
                    handleStatusUpdate(userId, status.id);
                  }
                }}
              >
                {status.name}
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const handleRoleUpdate = async (userId, newPositionId) => {
    if (!token) {
      console.error("Token missing, cannot update role.");
      return;
    }
    try {
      await updateRole(userId, newPositionId, token); // ใช้จาก HandlersMember
      setSelectedPositions(prev => ({ ...prev, [userId]: newPositionId }));
    } catch (error) {
      // ข้อผิดพลาดถูกจัดการใน HandlersMember
    }
  };

  const renderPositionDropdown = (userId) => {
    if (!Array.isArray(positions) || positions.length === 0) {
      return <span className="text-muted small">{translations.loadingPositions || 'Loading...'}</span>;
    }
    const selectedPositionId = selectedPositions[userId];
    const currentPosition = positions.find(p => p.position_ID === selectedPositionId);
    const currentPosName = currentPosition ? currentPosition.position_Name : (translations.selectPosition || "Select Position");

    return (
      <div className="dropdown">
        <button className="btn btn-secondary dropdown-toggle btn-sm" type="button" data-bs-toggle="dropdown" aria-expanded="false">
          <FontAwesomeIcon icon={faUsersGear} /> {currentPosName}
        </button>
        <ul className="dropdown-menu">
          {positions.map((position) => (
            <li key={position.position_ID}>
              <a
                className={`dropdown-item ${position.position_ID === selectedPositionId ? 'active' : ''}`}
                href="#!"
                onClick={(event) => {
                  event.preventDefault();
                  if (position.position_ID !== selectedPositionId) {
                    handleRoleUpdate(userId, position.position_ID);
                  }
                }}
              >
                {position.position_Name}
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const handleChangePassword = (userId) => {
    if (navigate) {
      navigate(`/change-password?userId=${userId}`);
    } else {
      console.error("Navigate function was not passed to MemberTable component.");
    }
  };

  const getRowStyle = (positionId) => {
    switch (positionId) {
      case 1:
        return { backgroundColor: '#aeeaf4' };
      case 2:
        return { backgroundColor: '#fde7e4' };
      default:
        return { backgroundColor: '#e9f8fb' };
    }
  };

  if (!memberData || memberData.length === 0) {
    return <div className="text-center p-3">{translations.noMembersFound || "No members found."}</div>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead className="table-light">
          <tr>
            <th>{translations.firstName || "First Name"}</th>
            <th>{translations.surName || "Sur Name"}</th>
            <th>{translations.email || "Email"}</th>
            <th>{translations.telephone || "Telephone"}</th>
            <th>{translations.birthday || "Birthday"}</th>
            <th>{translations.contact || "Contact"}</th>
            <th>{translations.specific || "Specific"}</th>
            <th>{translations.actions || "Actions"}</th>
          </tr>
        </thead>
        <tbody>
          {memberData.map((member, index) => {
            if (!member || typeof member.user_ID === 'undefined') return null;
            const rowStyle = getRowStyle(selectedPositions[member.user_ID]);
            return (
              <tr key={member.user_ID || index}>
                <td style={rowStyle}>{member.first_name}</td>
                <td>{member.sur_name}</td>
                <td>{member.email}</td>
                <td>{member.telephone}</td>
                <td>{member.birthday ? new Date(member.birthday).toLocaleDateString() : ''}</td>
                <td>{member.contact}</td>
                <td>
                  <button
                    className="btn btn-info me-2 btn-sm"
                    onClick={() => onViewSpecific(member.user_ID)}
                    title={translations.viewSpecificTitle || "View Specific Details"}
                  >
                    <FontAwesomeIcon icon={faIdBadge} />
                  </button>
                </td>
                <td style={rowStyle}>
                  <div className="d-flex align-items-center">
                    <button className="btn btn-primary me-2 btn-sm" onClick={() => onEditMember(member)}>
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                    <div className="me-2">
                      {renderPositionDropdown(member.user_ID)}
                    </div>
                    <div className="me-2">
                      {renderStatusDropdown(member.user_ID)}
                    </div>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleChangePassword(member.user_ID)}
                      title={translations.changePasswordTitle || "Change Password"}
                    >
                      <FontAwesomeIcon icon={faKey} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MemberTable;